import { useQuery } from "@tanstack/react-query";
import { type Key, useEffect, useMemo, useRef, useState } from "react";
import { useListData } from "react-stately";
import { AddressPicker } from "@/components/application/address-picker/address-picker";
import { SignaturePad } from "@/components/application/signature-pad/signature-pad";
import {
	UserPicker,
	userFieldValueFromIds,
} from "@/components/application/user-picker/user-picker";
import { MultiSelect } from "@/components/base/select/multi-select";
import type { SelectItemType } from "@/components/base/select/select";
import type { DisplayFieldFeedback as DisplayFieldFeedbackType } from "@/components/Atoms/DisplayField/display-field.shared";
import { LabelWithFeedback } from "@/components/base/input/label-with-feedback";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDynamicFormHost } from "@/lib/dynamicForms/contexts/dynamic-form-host-context";
import { useDynamicFormContext } from "@/lib/dynamicForms/contexts/DynamicFormContext";
import type {
	AddressField,
	MultiSelectField,
	SignatureField,
	UsersField,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import type { FieldRendererProps } from "@/lib/dynamicForms/interfaces/renderer.interface";
import { storedFileId } from "@/lib/dynamicForms/utils/stored-file-id.util";
import { storedStringList } from "@/lib/dynamicForms/utils/stored-string-list.util";
import { apiService, organizationIdHeader } from "@/services/api.service";
import { organizationService } from "@/services/organization/organization.service";
import { showApiErrorToast } from "@/utils/toast.utils";

function useContinuousFieldEscape(fieldName: string, fieldValue: unknown) {
	const ctx = useDynamicFormContext();
	return {
		onFocus: () => {
			ctx?.handleFieldFocus(fieldName, fieldValue);
		},
		onKeyDown: (event: {
			key: string;
			preventDefault: () => void;
			stopPropagation: () => void;
		}) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			ctx?.handleFieldCancel(fieldName);
		},
	};
}

export function MultiSelectFormControl({
	field,
	fieldApi,
	feedback,
}: FieldRendererProps) {
	const multiSelect = field as MultiSelectField;
	const selected = storedStringList(fieldApi.state.value);
	const items = useMemo<SelectItemType[]>(
		() =>
			multiSelect.options.map((option) => ({
				id: option.value,
				label: option.label,
			})),
		[multiSelect.options],
	);
	const selectedItems = useListData<SelectItemType>({ initialItems: [] });

	useEffect(() => {
		const desired = items.filter((item) => selected.includes(item.id));
		const currentIds = selectedItems.items.map((item) => item.id).join("\0");
		const desiredIds = desired.map((item) => item.id).join("\0");
		if (currentIds === desiredIds) return;
		for (const item of [...selectedItems.items]) {
			selectedItems.remove(item.id);
		}
		for (const item of desired) {
			selectedItems.append(item);
		}
	}, [items, selected, selectedItems]);

	return (
		<div className="flex flex-col gap-1.5">
			<LabelWithFeedback
				label={multiSelect.label}
				isRequired={multiSelect.required}
				isFilled={selected.length > 0}
				feedback={feedback as DisplayFieldFeedbackType | undefined}
			/>
			<MultiSelect
				aria-label={multiSelect.label}
				isRequired={multiSelect.required}
				isDisabled={multiSelect.disabled}
				isInvalid={fieldApi.state.meta.errors.length > 0}
				hint={fieldApi.state.meta.errors[0] || multiSelect.hint || undefined}
				placeholder={multiSelect.placeholder}
				items={items}
				selectedItems={selectedItems}
				placeholderIcon={null}
				showTagAvatar={false}
				onItemInserted={(key: Key) => {
					const next = String(key);
					if (selected.includes(next)) return;
					fieldApi.handleChange(userFieldValueFromIds([...selected, next]));
				}}
				onItemCleared={(key: Key) => {
					fieldApi.handleChange(
						userFieldValueFromIds(
							selected.filter((entry) => entry !== String(key)),
						),
					);
				}}
			>
				{(item) => (
					<MultiSelect.Item id={item.id}>{item.label}</MultiSelect.Item>
				)}
			</MultiSelect>
		</div>
	);
}

export function UsersFormControl({
	field,
	fieldApi,
	feedback,
}: FieldRendererProps) {
	const usersField = field as UsersField;
	const { t } = useAppTranslation();
	const host = useDynamicFormHost();
	const selected = storedStringList(fieldApi.state.value);
	return (
		<UserPicker
			organizationId={host?.organizationId ?? ""}
			idKind="organizationUser"
			selectionMode="multiple"
			label={usersField.label}
			isRequired={usersField.required}
			isDisabled={usersField.disabled}
			isInvalid={fieldApi.state.meta.errors.length > 0}
			hint={fieldApi.state.meta.errors[0] || usersField.hint || undefined}
			feedback={feedback}
			placeholder={
				usersField.placeholder || t("common.userPicker.placeholder")
			}
			value={selected.length === 0 ? null : selected}
			onChange={(next) => {
				fieldApi.handleChange(next);
			}}
		/>
	);
}

export function AddressFormControl({
	field,
	fieldApi,
	feedback,
}: FieldRendererProps) {
	const addressField = field as AddressField;
	const { t } = useAppTranslation();
	const value = String(fieldApi.state.value ?? "");
	const escapeProps = useContinuousFieldEscape(addressField.name, value);
	return (
		<AddressPicker
			label={addressField.label}
			isRequired={addressField.required}
			isDisabled={addressField.disabled}
			isInvalid={fieldApi.state.meta.errors.length > 0}
			hint={fieldApi.state.meta.errors[0] || addressField.hint || undefined}
			feedback={feedback}
			placeholder={
				addressField.placeholder || t("common.addressPicker.placeholder")
			}
			value={value}
			onChange={(next) => {
				fieldApi.handleChange(next);
			}}
			onBlur={fieldApi.handleBlur}
			onFocus={escapeProps.onFocus}
			onKeyDown={escapeProps.onKeyDown}
		/>
	);
}

function signatureBlobCanPreview(blob: Blob): boolean {
	if (blob.size === 0) return false;
	const type = blob.type.toLowerCase();
	return (
		type.length === 0 ||
		type.startsWith("image/") ||
		type === "application/octet-stream"
	);
}

export function SignatureFormControl({
	field,
	fieldApi,
	feedback,
}: FieldRendererProps) {
	const signatureField = field as SignatureField;
	const host = useDynamicFormHost();
	const { t } = useAppTranslation();
	const fileId = storedFileId(fieldApi.state.value);
	const runtime =
		host?.runtime === true &&
		Boolean(host.organizationId) &&
		Boolean(host.entityType) &&
		Boolean(host.entityId);
	const captureGeneration = useRef(0);
	const imageQuery = useQuery({
		queryKey: [
			"dynamic-form-signature",
			host?.entityType,
			host?.organizationId,
			host?.entityId,
			fileId,
		],
		enabled: runtime && Boolean(fileId),
		queryFn: () =>
			apiService.get<Blob>(
				`/files/${host?.entityType}/${host?.organizationId}/${host?.entityId}/${fileId}`,
				{
					responseType: "blob",
					headers: {
						Accept: "image/png,image/*,*/*",
						...organizationIdHeader(host?.organizationId),
					},
				},
			),
	});
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	useEffect(() => {
		const blob = imageQuery.data;
		if (!(blob instanceof Blob) || !signatureBlobCanPreview(blob)) {
			setImageUrl(null);
			return;
		}
		const url = URL.createObjectURL(blob);
		setImageUrl(url);
		return () => {
			URL.revokeObjectURL(url);
		};
	}, [imageQuery.data]);

	return (
		<div className="flex flex-col gap-1.5">
			<LabelWithFeedback
				label={signatureField.label}
				isRequired={signatureField.required}
				isFilled={Boolean(storedFileId(fieldApi.state.value))}
				feedback={feedback}
			/>
			<SignaturePad
				interactive={runtime}
				disabled={signatureField.disabled}
				placeholder={t("admin.shared.formEditor.properties.signaturePlaceholder")}
				clearLabel={t("common.signaturePad.clear")}
				imageUrl={imageUrl}
				onCapture={(file) => {
					if (!host?.organizationId) return;
					const generation = ++captureGeneration.current;
					void organizationService
						.uploadTemporaryOrganizationFile(host.organizationId, file)
						.then((id) => {
							if (captureGeneration.current !== generation) return;
							fieldApi.handleChange(id);
						})
						.catch((error: unknown) => {
							if (captureGeneration.current !== generation) return;
							showApiErrorToast({
								error,
								message: t("common.signaturePad.uploadFailed"),
							});
						});
				}}
				onClear={() => {
					captureGeneration.current += 1;
					fieldApi.handleChange(null);
				}}
			/>
			{signatureField.hint ? (
				<p className="text-sm text-tertiary">{signatureField.hint}</p>
			) : null}
		</div>
	);
}
